import { Injectable, inject, WritableSignal } from '@angular/core';
import { Product, PackagingOption } from '../models/product.model';
import { PosDataService } from './pos-data.service';
import { CartStateService } from './cart-state.service';

/** Ajout d'une ligne au panier — `CartService.addToCart`, passé en rappel pour éviter un cycle. */
export type AjouterAuPanier = (
    product: Product,
    variant: string,
    packaging: PackagingOption,
    qty: number,
) => unknown;

/** Ce que la synchronisation lit et écrit côté `CartService`. */
export interface ContexteAjoutsOfferts {
    /** Quantités déjà ajoutées automatiquement : "productId" ou "productId:variantId" → qté. */
    autoAddedQuantities: WritableSignal<Record<string, number>>;
    ajouter: AjouterAuPanier;
}

/**
 * Ajouts et retraits automatiques des articles offerts (BOGO inter-produits).
 *
 * Sorti de `CartService`, au-delà de la limite de 500 lignes. L'ajout passe par
 * le rappel `ajouter` plutôt que par une dépendance sur `CartService`, qui
 * dépend de ce service.
 */
@Injectable({
    providedIn: 'root'
})
export class CartAutoAddService {
    private posData = inject(PosDataService);
    private state = inject(CartStateService);

    /**
     * Sync BOGO auto-added items (cross-product BOGO).
     * The backend returns benefitEntitlements: { "productId:variantId": qty }
     * We auto-add/remove items in the cart to match.
     */
    synchroniser(entitlements: Record<string, number>, contexte: ContexteAjoutsOfferts): boolean {
        const currentCart = this.state.items();
        const autoAdded = contexte.autoAddedQuantities();
        const newAutoAdded: Record<string, number> = { ...autoAdded };
        let cartChanged = false;

        const allKeys = new Set([...Object.keys(entitlements), ...Object.keys(newAutoAdded)]);

        allKeys.forEach(key => {
            const targetQty = entitlements[key] || 0;
            const currentQty = newAutoAdded[key] || 0;
            const diff = targetQty - currentQty;

            if (diff === 0) return;

            const [prodIdStr, variantIdStr] = key.split(':');
            const productId = prodIdStr;
            const variantId = variantIdStr ? Number(variantIdStr) : undefined;

            if (diff > 0) {
                // Need to ADD items
                const products = this.posData.products();
                const product = products.find(p => String(p.id).toLowerCase() === productId.toLowerCase());

                if (product) {
                    // Check stock availability
                    const stock = product.stock;
                    let totalAvailable = 0;
                    if (product.productKind === 'SERVICE') {
                        totalAvailable = Number.POSITIVE_INFINITY;
                    } else if (variantId && stock?.byVariant) {
                        const vs = stock.byVariant.find(v => v.variantId === variantId);
                        totalAvailable = vs?.quantity || 0;
                    } else {
                        totalAvailable = stock?.total || 0;
                    }

                    const existingItem = currentCart.find(i => {
                        const matchProd = String(i.id).toLowerCase() === productId.toLowerCase();
                        const matchVar = variantId
                            ? (i.packaging.variantId === variantId)
                            : !i.packaging.variantId;
                        return matchProd && matchVar;
                    });
                    const currentInCart = existingItem ? existingItem.qty : 0;

                    if ((currentInCart + diff) <= totalAvailable) {
                        if (existingItem) {
                            this.state.items.update(items => items.map(i =>
                                i.cartId === existingItem.cartId
                                    ? { ...i, qty: i.qty + diff, lineTotalCents: undefined }
                                    : i
                            ));
                        } else {
                            // Find a default packaging for this product
                            const pkg = product.packaging?.[0];
                            if (pkg) {
                                const variantLabel = variantId
                                    ? this.posData.variants().find(v => v.id === variantId)?.label || ''
                                    : '';
                                const packagingOption: PackagingOption = {
                                    id: String(pkg.id),
                                    label: pkg.label || '',
                                    multiplier: pkg.totalPerBase || 1,
                                    iconName: '',
                                    salePrice: pkg.salePrice ?? undefined,
                                    unitId: pkg.unitId,
                                    variantId: pkg.variantId ?? undefined,
                                };
                                const productForCart: Product = {
                                    id: product.id,
                                    name: product.name,
                                    price: product.price,
                                    image: product.imageUrl || '',
                                    category: '',
                                    variants: [],
                                    sku: product.sku,
                                    commonName: product.commonName,
                                    imageUrl: product.imageUrl,
                                    familyId: product.familyId,
                                    // Sans le type, l'ajout reserverait le stock d'un service.
                                    productKind: product.productKind,
                                    stock: product.stock,
                                };
                                contexte.ajouter(productForCart, variantLabel, packagingOption, diff);
                            }
                        }
                        newAutoAdded[key] = currentQty + diff;
                        cartChanged = true;
                    }
                }
            } else {
                // Need to REMOVE items (diff < 0)
                const toRemove = Math.abs(diff);
                const existingItem = currentCart.find(i => {
                    const matchProd = String(i.id).toLowerCase() === productId.toLowerCase();
                    const matchVar = variantId
                        ? (i.packaging.variantId === variantId)
                        : !i.packaging.variantId;
                    return matchProd && matchVar;
                });

                if (existingItem) {
                    if (existingItem.qty > toRemove) {
                        this.state.items.update(items => items.map(i =>
                            i.cartId === existingItem.cartId
                                ? { ...i, qty: i.qty - toRemove, lineTotalCents: undefined }
                                : i
                        ));
                    } else {
                        this.state.items.update(items => items.filter(i => i.cartId !== existingItem.cartId));
                    }
                    newAutoAdded[key] = currentQty - toRemove;
                    cartChanged = true;
                } else {
                    newAutoAdded[key] = targetQty;
                    cartChanged = true;
                }
            }
        });

        if (cartChanged) {
            contexte.autoAddedQuantities.set(newAutoAdded);
        }
        return cartChanged;
    }
}
